import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Clock, X, ArrowRight, Trash2, Building2, Server } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Button } from './ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const secs = Math.max(0, Math.floor((now - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

/**
 * RecentViewsDropdown — clock icon next to the property selector.
 * Shows the last N property+node combos the user visited. Clicking one restores that view.
 * Public API:
 *  - useRecentViewsApi(token) → { push(entry), views, refresh() }
 *  - <RecentViewsDropdown token onJump />
 */
export function useRecentViewsApi(token) {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND}/api/user/recent-views`, { headers });
      setViews(r.data.views || []);
    } catch { /* empty */ }
    finally { setLoading(false); }
  }, [token, headers]);

  useEffect(() => { refresh(); }, [refresh]);

  const push = useCallback(async (entry) => {
    if (!token || !entry?.property_id || !entry?.node_id) return;
    try {
      await axios.post(`${BACKEND}/api/user/recent-views`, entry, { headers });
      // Optimistic local update so the dropdown stays fresh even between refreshes
      setViews(prev => {
        const key = `${entry.property_id}::${entry.node_id}`;
        const filtered = prev.filter(v => v.key !== key);
        return [{ ...entry, key, visited_at: new Date().toISOString() }, ...filtered].slice(0, 20);
      });
    } catch { /* empty */ }
  }, [token, headers]);

  const remove = useCallback(async (key) => {
    try {
      await axios.delete(`${BACKEND}/api/user/recent-views/${encodeURIComponent(key)}`, { headers });
      setViews(prev => prev.filter(v => v.key !== key));
    } catch { /* empty */ }
  }, [headers]);

  const clear = useCallback(async () => {
    try {
      await axios.delete(`${BACKEND}/api/user/recent-views`, { headers });
      setViews([]);
    } catch { /* empty */ }
  }, [headers]);

  return { views, loading, refresh, push, remove, clear };
}

export default function RecentViewsDropdown({ token, onJump, size = 'md' }) {
  const { views, refresh, remove, clear } = useRecentViewsApi(token);
  const [open, setOpen] = useState(false);

  // Refresh on open so counts stay in sync with other tabs
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // Keyboard shortcut: `R` (when not typing) opens the dropdown
  useEffect(() => {
    const handler = (e) => {
      if (e.repeat) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const dim = size === 'sm' ? 'h-7 w-7' : 'h-8 w-8 md:h-9 md:w-9';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={`${dim} shrink-0 relative`}
                data-testid="recent-views-trigger"
                aria-label="Recent views"
              >
                <Clock className="h-4 w-4" />
                {views.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                    {views.length > 9 ? '9+' : views.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Recent views (R)</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent align="start" className="w-[340px] p-0" data-testid="recent-views-popover">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Clock className="h-3.5 w-3.5" /> Recent Views
          </div>
          {views.length > 0 && (
            <button
              onClick={clear}
              className="text-[10px] text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
              data-testid="recent-views-clear"
            >
              <Trash2 className="h-3 w-3" /> Clear
            </button>
          )}
        </div>

        {views.length === 0 ? (
          <div className="py-8 px-4 text-center text-xs text-muted-foreground">
            <Clock className="h-6 w-6 mx-auto mb-2 opacity-30" />
            No recent views yet.<br />
            <span className="text-[10px]">Pick a property and click a node to start.</span>
          </div>
        ) : (
          <div className="max-h-[380px] overflow-y-auto py-1" data-testid="recent-views-list">
            {views.map((v) => (
              <div
                key={v.key}
                className="group flex items-center gap-2 px-3 py-2 hover:bg-muted/60 cursor-pointer transition-colors border-l-2 border-l-transparent hover:border-l-primary"
                onClick={() => { setOpen(false); onJump && onJump(v); }}
                data-testid={`recent-view-${v.key}`}
              >
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-lg bg-muted/60 flex items-center justify-center">
                    <Server className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate">{v.node_name}</div>
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground truncate">
                    <Building2 className="h-2.5 w-2.5 shrink-0" />
                    <span className="truncate">{v.property_name || 'Overall'}</span>
                    <span className="mx-1 opacity-50">·</span>
                    <span className="shrink-0">{timeAgo(v.visited_at)}</span>
                  </div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                <button
                  onClick={(e) => { e.stopPropagation(); remove(v.key); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity h-5 w-5 rounded flex items-center justify-center hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  title="Remove"
                  data-testid={`recent-view-remove-${v.key}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
